import {
  Activity,
  CircleDollarSign,
  Clock,
  Gauge,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  MessagesSquare,
  Mic,
  TrendingUp,
  Waypoints,
} from 'lucide-react'
import { BackButton } from 'primitives/back-button'
import { Typography } from 'primitives/typography'
import { useMemo } from 'react'
import { Link, useLocation } from 'router-v6'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../../primitives/sidebar'

type ReportItem = { title: string; url: string; icon: LucideIcon }
type ReportSection = { label: string; items: ReportItem[] }

// AI-agent reports. Behind the `voiceAgentsSplit` flag the Voice Agent report is split into focused
// sub-reports (Overview / AI Performance / Outcomes; Overview also carries conversation quality).
// Without the flag it stays a single "Voice Agent" item. Sibling URLs are kept non-prefix-
// overlapping so the startsWith() active check stays unambiguous.
const AI_AGENT_ITEMS_SPLIT: ReportItem[] = [
  { title: 'Overview', url: '/analytics/voice-agents/overview', icon: LayoutDashboard },
  { title: 'AI Agent Performance', url: '/analytics/voice-agents/ai-performance', icon: Gauge },
  { title: 'Outcomes & Goals', url: '/analytics/voice-agents/outcomes', icon: Waypoints },
  { title: 'Campaign & Performance', url: '/analytics/voice-agents/campaign', icon: Megaphone },
]

const AI_AGENT_ITEMS_LEGACY: ReportItem[] = [
  { title: 'Voice Agent', url: '/analytics/voice-agents', icon: Mic },
]

// Human-specialist + general reports. (Names are intentionally unchanged for now — the
// section split is what's shipping; renames come later.)
const HUMAN_SPECIALIST_ITEMS: ReportItem[] = [
  { title: 'Customer Metrics', url: '/analytics/customer-metrics', icon: TrendingUp },
  {
    title: 'Conversations Overview',
    url: '/analytics/conversations-overview',
    icon: MessagesSquare,
  },
  { title: 'Specialist Performance', url: '/analytics/agent-performance', icon: Activity },
  { title: 'Specialist Availability', url: '/analytics/agent-availability', icon: Clock },
  { title: 'Operations Overview', url: '/analytics/operations-overview', icon: LayoutDashboard },
]

// Usage (billed volume/spend from UsageSummary). Three separate reports, each a route-driven tab
// of the same page (/analytics/usage/:tab). Overview is the default landing report.
const USAGE_ITEMS: ReportItem[] = [
  { title: 'Overall Spend', url: '/analytics/usage/overview', icon: CircleDollarSign },
  { title: 'Messaging', url: '/analytics/usage/messaging', icon: MessagesSquare },
  { title: 'Voice', url: '/analytics/usage/voice', icon: Mic },
]

export interface ReportsConfig {
  isHumanSpecialistEnabled?: boolean
  hasFlowPermission?: boolean
  // Voice Agents redesign flag (per-org). When true, AI Agents shows the split sub-reports;
  // otherwise a single "Voice Agent" item.
  voiceAgentsSplit?: boolean
  usageEnabled?: boolean
}

// AI Agents on top, Human Specialist next, Usage last. Each section renders only when the org has
// access: AI Agents needs flow permission, Human Specialist needs the human-specialist feature,
// Usage needs the usageReportsEnabled flag. With no config, show all.
const getReportSections = (config?: ReportsConfig): ReportSection[] => {
  const showAi = !config || Boolean(config.hasFlowPermission)
  const showHuman = !config || Boolean(config.isHumanSpecialistEnabled)
  const showUsage = !config || Boolean(config.usageEnabled)

  const sections: ReportSection[] = []
  const aiItems = config?.voiceAgentsSplit ? AI_AGENT_ITEMS_SPLIT : AI_AGENT_ITEMS_LEGACY
  if (showAi) sections.push({ label: 'AI Agents', items: aiItems })
  if (showHuman) sections.push({ label: 'Human Specialist', items: HUMAN_SPECIALIST_ITEMS })
  if (showUsage) sections.push({ label: 'Usage', items: USAGE_ITEMS })
  return sections
}

export const AppSidebarReports: React.FC<{ reportsConfig?: ReportsConfig }> = ({
  reportsConfig,
}) => {
  const location = useLocation()
  const sections = useMemo(
    () => getReportSections(reportsConfig),
    [
      reportsConfig?.isHumanSpecialistEnabled,
      reportsConfig?.hasFlowPermission,
      // Flag arrives async from hodor (false → true); without it here the nav stays on the
      // single legacy item even after the flag flips and the content switches to the split.
      reportsConfig?.voiceAgentsSplit,
      reportsConfig?.usageEnabled,
    ],
  )

  return (
    <>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <BackButton data-testid="reports-back-btn" />
          <Typography variant="P/600">Analytics</Typography>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {sections.length === 0 && (
          <div className="p-4 text-s-500 text-muted-foreground">No reports available.</div>
        )}
        {sections.map((section) => (
          <SidebarGroup key={section.label}>
            {/* Default size/color (text-xxs-600 / tertiary) + uppercase to match the main app
                sidebar's section headers — kept consistent across the app. */}
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const isActive = location.pathname.startsWith(item.url)
                  return (
                    <SidebarMenuItem key={item.title}>
                      {/* No `tooltip` prop: this panel always shows text labels, so the
                          primitive's collapsed-state tooltip would just repeat the visible label. */}
                      <SidebarMenuButton isActive={isActive} asChild>
                        <Link
                          to={item.url}
                          data-testid={`link-report-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="w-4 h-4" strokeWidth={isActive ? 2 : 1.5} />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </>
  )
}
